import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader, MapPin, X } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { SearchInput } from '@/components/search/SearchInput';
import { FilterPill } from '@/components/ui/FilterPill';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { useBrowseInstructors, dismissLocationPrompt } from '../hooks/useBrowseInstructors';
import { InstructorBrowseCard } from '../components/InstructorBrowseCard';
import type { ServiceFilter } from '../components/InstructorBrowseCard';
import type { BrowseInstructor } from '../hooks/useBrowseInstructors';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function matchesSearch(instructor: BrowseInstructor, term: string): boolean {
  const lower = term.toLowerCase();
  return (
    (instructor.full_name?.toLowerCase().includes(lower)      ?? false) ||
    (instructor.username?.toLowerCase().includes(lower)       ?? false) ||
    (instructor.jiu_jitsu_school?.toLowerCase().includes(lower) ?? false) ||
    (instructor.belt_rank?.toLowerCase().includes(lower)      ?? false)
  );
}

function filterByService(instructor: BrowseInstructor, filter: ServiceFilter): boolean {
  if (filter === 'video_review')    return instructor.accept_roll_reviews && instructor.is_premium;
  if (filter === 'private_classes') return instructor.has_public_agenda;
  return true;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// Tap-hint copy under the filter pills declares what tapping a card does
// in the current mode, so users coming from a context-specific CTA (e.g.
// "Roll Review" featured) immediately know the next action.
const HINT_KEY: Record<ServiceFilter, string> = {
  all:             'browse_instructors.tap_hint_all',
  video_review:    'browse_instructors.tap_hint_video_review',
  private_classes: 'browse_instructors.tap_hint_private_classes',
};

// Whitelist for the `?intent=` query param. Anything else falls back to 'all'
// so a stray/old link never crashes or shows a malformed filter.
const INTENT_TO_FILTER: Record<string, ServiceFilter> = {
  video_review:    'video_review',
  private_classes: 'private_classes',
};

const BrowseInstructors: React.FC = () => {
  const navigate   = useNavigate();
  const { t }      = useI18n();
  const [searchParams] = useSearchParams();

  // Pre-select the filter from `?intent=` on first render. Subsequent changes
  // come from the user tapping a pill, so we don't re-sync — once they switch
  // away, we honor their choice over the URL hint.
  const initialFilter: ServiceFilter =
    INTENT_TO_FILTER[searchParams.get('intent') ?? ''] ?? 'all';

  const [activeFilter,       setActiveFilter]       = useState<ServiceFilter>(initialFilter);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [locationDismissed,  setLocationDismissed]  = useState(false);

  const {
    instructors,
    counts,
    isLoading,
    error,
    refetch,
    showLocationBanner,
    locationRequested,
    requestLocation,
  } = useBrowseInstructors();

  const handleDismissLocation = () => {
    dismissLocationPrompt();
    setLocationDismissed(true);
  };

  const showBanner = showLocationBanner && !locationDismissed && !locationRequested;

  // Apply service filter + search (scroll resets to top on filter change per Edge Case 11)
  const displayedInstructors = useMemo(() => {
    const byService = instructors.filter(i => filterByService(i, activeFilter));
    if (!searchTerm.trim()) return byService;
    return byService.filter(i => matchesSearch(i, searchTerm));
  }, [instructors, activeFilter, searchTerm]);

  const handleFilterChange = (filter: ServiceFilter) => {
    setActiveFilter(filter);
    setSearchTerm('');
  };

  const handleCardTap = (instructor: BrowseInstructor) => {
    if (activeFilter === 'video_review') {
      // RequestRollReview expects price_cents / currency — map from BrowseInstructor shape.
      const instructorForCheckout = {
        ...instructor,
        price_cents: instructor.roll_review_price_cents,
        currency:    instructor.roll_review_currency,
      };
      // `entry_point` is forwarded to the RequestRollReview mount so the
      // analytics flow_started event reports where this session began.
      navigate(`/request-roll-review/${instructor.id}`, {
        state: { instructor: instructorForCheckout, entry_point: 'browse_instructors' },
      });
    } else if (activeFilter === 'private_classes') {
      navigate(`/instructor-slots/${instructor.username ?? instructor.id}`);
    } else {
      navigate(`/profile/${instructor.username ?? instructor.id}`);
    }
  };

  // Section label
  const sectionLabel = activeFilter === 'video_review'
    ? t('browse_instructors.section_video_review')
    : activeFilter === 'private_classes'
    ? t('browse_instructors.section_private_classes')
    : t('browse_instructors.section_all');

  const resultCount = searchTerm.trim()
    ? displayedInstructors.length
    : (activeFilter === 'video_review'
        ? counts.videoReview
        : activeFilter === 'private_classes'
        ? counts.privateClasses
        : counts.all);

  // Empty state messages
  const emptyTitle = searchTerm.trim()
    ? t('browse_instructors.empty_search')
    : activeFilter === 'video_review'
    ? t('browse_instructors.empty_video_review')
    : activeFilter === 'private_classes'
    ? t('browse_instructors.empty_private_classes')
    : t('browse_instructors.empty_all');

  const emptySub = searchTerm.trim()
    ? t('browse_instructors.empty_search_sub')
    : activeFilter === 'video_review'
    ? t('browse_instructors.empty_video_review_sub')
    : activeFilter === 'private_classes'
    ? t('browse_instructors.empty_private_classes_sub')
    : t('browse_instructors.empty_all_sub');

  return (
    <SubPageLayout title={t('browse_instructors.title')}>

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={() => setSearchTerm('')}
        onSearch={setSearchTerm}
        placeholder={t('browse_instructors.search_placeholder')}
      />

      {/* Location permission banner (AC #12 — contextual explanation before system prompt) */}
      {showBanner && (
        <div className="mt-3 flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/50" />
          <p className="flex-1 text-xs leading-relaxed text-white/60">
            {t('browse_instructors.location_prompt')}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0 ml-1">
            <button
              onClick={handleDismissLocation}
              className="p-1 text-white/30 hover:text-white/60 transition-colors"
              aria-label={t('browse_instructors.location_dismiss')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={requestLocation}
              className="text-xs font-semibold text-sossiety-red active:opacity-70 transition-opacity"
            >
              {t('browse_instructors.location_allow')}
            </button>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide mt-3">
        <FilterPill
          label={t('browse_instructors.filter_all')}
          count={counts.all}
          active={activeFilter === 'all'}
          onClick={() => handleFilterChange('all')}
        />
        <FilterPill
          label={t('browse_instructors.filter_video_review')}
          count={counts.videoReview}
          active={activeFilter === 'video_review'}
          onClick={() => handleFilterChange('video_review')}
        />
        <FilterPill
          label={t('browse_instructors.filter_private_classes')}
          count={counts.privateClasses}
          active={activeFilter === 'private_classes'}
          onClick={() => handleFilterChange('private_classes')}
        />
      </div>

      {/* Tap hint — single line, declares the action a card-tap will trigger
          in the current mode (e.g. "Tap an instructor to request a Roll
          Review" on the video_review filter). */}
      <p className="text-xs text-white/40 mt-1">
        {t(HINT_KEY[activeFilter])}
      </p>

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader className="w-8 h-8 animate-spin text-white/40" />
          <p className="text-sm text-white/40">{t('browse_instructors.loading')}</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-sm text-red-400/80 text-center">{t('browse_instructors.error_loading')}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            {t('browse_instructors.try_again')}
          </Button>
        </div>
      ) : (
        <>
          {/* Section label + count */}
          <div className="flex items-center justify-between pt-4 pb-1">
            <p className="text-[10px] font-bold tracking-widest uppercase text-white/30">
              {sectionLabel}
            </p>
            <p className="text-[10px] font-medium text-white/30">
              {t('browse_instructors.results_count', { count: resultCount })}
            </p>
          </div>

          {/* List */}
          {displayedInstructors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <p className="text-sm font-medium text-white">{emptyTitle}</p>
              <p className="text-xs text-white/40 text-center">{emptySub}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {displayedInstructors.map(instructor => (
                <InstructorBrowseCard
                  key={instructor.id}
                  instructor={instructor}
                  activeFilter={activeFilter}
                  onClick={() => handleCardTap(instructor)}
                />
              ))}
            </div>
          )}
        </>
      )}

    </SubPageLayout>
  );
};

export default BrowseInstructors;
